import React, { useState, useEffect } from 'react';

const RoomDetails = ({ onNavigate, userInfo }) => {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [notificationsOpen, setNotificationsOpen] = useState(false);
    const [roomData, setRoomData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // API Base URL
    const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://app.learninghub.ind.in/api';

    // Fetch room details from backend
    useEffect(() => {
        const fetchRoomDetails = async () => {
            try {
                setLoading(true);
                setError(null);

                const userId = userInfo?.userId || userInfo?.id;
                
                if (!userId) {
                    throw new Error('User ID not found');
                }

                const token = localStorage.getItem('token') || sessionStorage.getItem('token');
                const headers = {
                    'Content-Type': 'application/json',
                };

                if (token) {
                    headers['Authorization'] = `Bearer ${token}`;
                }

                // Fetch student profile
                const response = await fetch(
                    `${API_BASE_URL}/student/profile/${userId}`,
                    { method: 'GET', headers }
                );

                if (!response.ok) {
                    throw new Error('Failed to fetch room details');
                }

                const data = await response.json();

                if (data.success && data.data) {
                    setRoomData(data.data);
                } else {
                    throw new Error(data.message || 'Failed to load room details');
                }

            } catch (err) {
                console.error('Error fetching room details:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchRoomDetails();
    }, [userInfo, API_BASE_URL]);

    // Format currency
    const formatCurrency = (amount) => {
        return `₹${parseFloat(amount || 0).toLocaleString('en-IN')}`;
    };

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric', 
            year: 'numeric' 
        });
    };

    const notifications = [
        {
            id: 1,
            type: 'payment',
            title: 'Payment Reminder',
            message: roomData?.paymentDueDate 
                ? `Your hostel fee payment is due on ${formatDate(roomData.paymentDueDate)}`
                : 'Payment information not available',
            time: '2h ago',
            icon: '💰',
            unread: true
        }
    ];

    const MenuItem = ({ icon, label, active = false, route }) => (
        <div
            className={`menu-item ${active ? 'menu-item-active' : ''}`}
            onClick={() => {
                if (route && onNavigate) {
                    onNavigate(route);
                }
                setSidebarOpen(false);
            }}
        >
            <span className="menu-icon">{icon}</span>
            <span className="menu-label">{label}</span>
            <div className="menu-glow"></div>
        </div>
    );

    const handleLogout = () => {
        if (onNavigate) {
            onNavigate('hostel-login');
        }
    };

    // Loading State
    if (loading) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                backgroundColor: '#ffffff',
                flexDirection: 'column',
                gap: '20px'
            }}>
                <div style={{
                    width: '60px',
                    height: '60px',
                    border: '6px solid #f3f3f3',
                    borderTop: '6px solid #2b6777',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                }}></div>
                <p style={{ 
                    color: '#2b6777', 
                    fontSize: '18px', 
                    fontWeight: '600' 
                }}>Loading Room Details...</p>
                <style>{`
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                `}</style>
            </div>
        );
    }

    // Error State
    if (error) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                backgroundColor: '#f4f7fa',
                padding: '2rem'
            }}>
                <div style={{
                    backgroundColor: 'white',
                    borderRadius: '16px',
                    padding: '3rem',
                    textAlign: 'center',
                    maxWidth: '500px',
                    boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
                }}>
                    <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>⚠️</div>
                    <h3 style={{ color: '#dc2626', marginBottom: '1rem', fontSize: '1.5rem' }}>
                        Error Loading Room Details
                    </h3>
                    <p style={{ color: '#666', marginBottom: '2rem' }}>{error}</p>
                    <button
                        onClick={() => window.location.reload()}
                        style={{
                            backgroundColor: '#2b6777',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '0.75rem 1.5rem',
                            cursor: 'pointer',
                            fontSize: '1rem',
                            fontWeight: '600'
                        }}
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    // Prepare stats from room data - only show available data
    const stats = [];
    
    if (roomData) {
        // Room Number stat (always show if room data exists)
        if (roomData.roomNumber) {
            stats.push({ 
                icon: '🏠', 
                label: 'Room Number', 
                value: roomData.roomNumber, 
                subtext: roomData.blockName ? `Block ${roomData.blockName}` : 'Room Assigned'
            });
        }
        
        // Monthly Rent stat (only show if balance exists)

        // Occupancy stat (only show if room type exists)
        if (roomData.roomType) {
            let occupancyValue = 'N/A';
            if (roomData.roomType === 'single') occupancyValue = '1/1';
            else if (roomData.roomType === 'double') occupancyValue = '2/2';
            else if (roomData.roomType === 'triple') occupancyValue = '3/3';
            else occupancyValue = roomData.roomType.charAt(0).toUpperCase() + roomData.roomType.slice(1);
            
            stats.push({ 
                icon: '👥', 
                label: 'Occupancy', 
                value: occupancyValue,
                subtext: roomData.roomType.charAt(0).toUpperCase() + roomData.roomType.slice(1) + ' Sharing'
            });
        }
        
        // Floor stat (only show if floor number exists)
        if (roomData.floorNumber !== null && roomData.floorNumber !== undefined) {
            stats.push({ 
                icon: '📍', 
                label: 'Floor Number', 
                value: `Floor ${roomData.floorNumber}`,
                subtext: roomData.blockName ? `Block ${roomData.blockName}` : 'Location'
            });
        }
    }

    // Prepare roommate info - only show current user from real data
    const roommates = [];
    
    if (roomData) {
        roommates.push({
            name: roomData.fullName || 'You',
            rollNo: roomData.admissionNumber || 'N/A',
            department: roomData.course || 'N/A',
            year: roomData.yearOfStudy ? `${roomData.yearOfStudy}${roomData.yearOfStudy === 1 ? 'st' : roomData.yearOfStudy === 2 ? 'nd' : roomData.yearOfStudy === 3 ? 'rd' : 'th'} Year` : 'N/A',
            avatar: roomData.fullName ? roomData.fullName.charAt(0).toUpperCase() : 'Y',
            contact: roomData.phoneNumber || 'N/A',
            isYou: true
        });
    }

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');

                * {
                    margin: 0;
                    padding: 0;
                    box-sizing: border-box;
                }

                body {
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
                    -webkit-font-smoothing: antialiased;
                    overflow-x: hidden;
                }

                @keyframes slideIn {
                    from {
                        opacity: 0;
                        transform: translateY(30px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                @keyframes shimmer {
                    0% { background-position: -1000px 0; }
                    100% { background-position: 1000px 0; }
                }

                @keyframes pulse {
                    0%, 100% { transform: scale(1); }
                    50% { transform: scale(1.05); }
                }

                @keyframes glow {
                    0%, 100% { opacity: 0.5; }
                    50% { opacity: 1; }
                }

                .dashboard-container {
                    display: flex;
                    height: 100vh;
                    background: #ffffff;
                    position: relative;
                    overflow: hidden;
                }

                /* Sidebar */
                .sidebar {
                    position: fixed;
                    top: 0;
                    left: 0;
                    bottom: 0;
                    width: 300px;
                    background: linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(242,242,242,0.98) 100%);
                    backdrop-filter: blur(30px);
                    border-right: 2px solid rgba(82,171,152,0.2);
                    transform: translateX(-100%);
                    transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
                    z-index: 100;
                    display: flex;
                    flex-direction: column;
                    box-shadow: 8px 0 40px rgba(43,103,119,0.15);
                }

                .sidebar.open {
                    transform: translateX(0);
                }

                .sidebar-header {
                    padding: 36px 28px;
                    background: linear-gradient(135deg, rgba(43,103,119,0.05) 0%, rgba(82,171,152,0.05) 100%);
                    border-bottom: 2px solid rgba(82,171,152,0.15);
                    position: relative;
                    overflow: hidden;
                }

                .sidebar-header::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: -100%;
                    width: 100%;
                    height: 100%;
                    background: linear-gradient(90deg, transparent, rgba(82,171,152,0.15), transparent);
                    animation: shimmer 4s infinite;
                }

                .sidebar-top {
                    display: flex;
                    align-items: center;
                    justify-content: flex-end;
                    margin-bottom: 24px;
                    position: relative;
                    z-index: 1;
                }

                .close-btn {
                    background: rgba(43,103,119,0.08);
                    border: 1.5px solid rgba(82,171,152,0.25);
                    cursor: pointer;
                    font-size: 20px;
                    color: #2b6777;
                    width: 38px;
                    height: 38px;
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.3s;
                }

                .close-btn:hover {
                    background: rgba(43,103,119,0.15);
                    border-color: #52ab98;
                    transform: rotate(90deg);
                }

                .user-info-sidebar {
                    display: flex;
                    align-items: center;
                    gap: 14px;
                    position: relative;
                    z-index: 1;
                }

                .user-avatar-sidebar {
                    width: 56px;
                    height: 56px;
                    border-radius: 50%;
                    background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-weight: 800;
                    font-size: 22px;
                    color: white;
                    box-shadow: 0 6px 24px rgba(43,103,119,0.3);
                    border: 3px solid rgba(255, 255, 255, 0.5);
                }

                .user-details-sidebar {
                    flex: 1;
                }

                .user-name-sidebar {
                    font-size: 16px;
                    font-weight: 800;
                    color: #2b6777;
                    margin-bottom: 4px;
                }

                .user-role-sidebar {
                    font-size: 13px;
                    color: #52ab98;
                    font-weight: 600;
                }

                .sidebar-nav {
                    flex: 1;
                    padding: 28px 20px;
                    overflow-y: auto;
                }

                .sidebar-nav::-webkit-scrollbar {
                    width: 6px;
                }

                .sidebar-nav::-webkit-scrollbar-track {
                    background: rgba(82,171,152,0.08);
                }

                .sidebar-nav::-webkit-scrollbar-thumb {
                    background: rgba(82,171,152,0.3);
                    border-radius: 3px;
                }

                .menu-item {
                    position: relative;
                    display: flex;
                    align-items: center;
                    gap: 14px;
                    padding: 16px 18px;
                    margin-bottom: 10px;
                    cursor: pointer;
                    color: #2b6777;
                    font-size: 15px;
                    font-weight: 600;
                    border-radius: 14px;
                    transition: all 0.3s;
                    overflow: hidden;
                }

                .menu-glow {
                    position: absolute;
                    inset: 0;
                    background: linear-gradient(135deg, rgba(43,103,119,0.08) 0%, rgba(82,171,152,0.08) 100%);
                    opacity: 0;
                    transition: opacity 0.3s;
                }

                .menu-item:hover {
                    color: #52ab98;
                    background: rgba(82,171,152,0.1);
                    transform: translateX(6px);
                    box-shadow: 0 4px 16px rgba(82,171,152,0.15);
                }

                .menu-item:hover .menu-glow {
                    opacity: 1;
                }

                .menu-item-active {
                    background: linear-gradient(135deg, rgba(43,103,119,0.15) 0%, rgba(82,171,152,0.15) 100%);
                    color: #2b6777;
                    box-shadow: 0 6px 20px rgba(82,171,152,0.2);
                    font-weight: 700;
                }

                .menu-item-active .menu-glow {
                    opacity: 1;
                    animation: glow 2s ease-in-out infinite;
                }

                .menu-icon {
                    font-size: 22px;
                    position: relative;
                    z-index: 1;
                }

                .menu-label {
                    position: relative;
                    z-index: 1;
                }

                .logout-section {
                    padding: 20px;
                    border-top: 2px solid rgba(82,171,152,0.15);
                }

                .logout-btn {
                    width: 100%;
                    padding: 16px 20px;
                    background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
                    color: white;
                    border: none;
                    border-radius: 14px;
                    font-size: 15px;
                    font-weight: 700;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 10px;
                    transition: all 0.3s;
                    box-shadow: 0 6px 24px rgba(43,103,119,0.3);
                }

                .logout-btn:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 10px 35px rgba(43,103,119,0.4);
                }

                /* Overlay */
                .overlay {
                    position: fixed;
                    inset: 0;
                    background: rgba(43,103,119,0.4);
                    backdrop-filter: blur(6px);
                    z-index: 90;
                    opacity: 0;
                    visibility: hidden;
                    transition: all 0.3s ease;
                }

                .overlay.show {
                    opacity: 1;
                    visibility: visible;
                }

                /* Main Content */
                .main-content {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    overflow: hidden;
                    width: 100%;
                    position: relative;
                    z-index: 1;
                }

                /* Top Header */
                .top-header {
                    background: #ffffff;
                    padding: 24px 20px;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    border-bottom: 1px solid rgba(82,171,152,0.12);
                    position: relative;
                    box-shadow: 0 2px 8px rgba(43,103,119,0.06);
                }

                .top-header::after {
                    content: '';
                    position: absolute;
                    bottom: 0;
                    left: 0;
                    right: 0;
                    height: 3px;
                    background: linear-gradient(90deg, #2b6777 0%, #52ab98 100%);
                }

                .header-left {
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    flex: 1;
                }

                .menu-toggle {
                    background: linear-gradient(135deg, rgba(43,103,119,0.1) 0%, rgba(82,171,152,0.1) 100%);
                    border: 2px solid rgba(82,171,152,0.25);
                    cursor: pointer;
                    font-size: 24px;
                    color: #2b6777;
                    width: 44px;
                    height: 44px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.3s;
                    box-shadow: 0 4px 20px rgba(43,103,119,0.15);
                    flex-shrink: 0;
                }

                .menu-toggle:hover {
                    transform: scale(1.05);
                    box-shadow: 0 6px 30px rgba(43,103,119,0.25);
                    background: linear-gradient(135deg, rgba(43,103,119,0.15) 0%, rgba(82,171,152,0.15) 100%);
                }

                .header-title-section {
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                    min-width: 0;
                    flex: 1;
                }

                .header-greeting {
                    font-size: 13px;
                    color: #52ab98;
                    font-weight: 600;
                    display: none;
                }

                .header-name {
                    font-size: 18px;
                    font-weight: 900;
                    background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    background-clip: text;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }

                .header-right {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    flex-shrink: 0;
                }

                .notification-btn {
                    position: relative;
                    background: rgba(82,171,152,0.1);
                    border: 2px solid rgba(82,171,152,0.2);
                    cursor: pointer;
                    font-size: 20px;
                    color: #2b6777;
                    width: 44px;
                    height: 44px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.3s;
                }

                .notification-btn:hover,
                .notification-btn.active {
                    background: rgba(82,171,152,0.2);
                    border-color: #52ab98;
                    box-shadow: 0 6px 24px rgba(82,171,152,0.3);
                    transform: scale(1.05);
                }

                .notification-badge {
                    position: absolute;
                    top: 4px;
                    right: 4px;
                    min-width: 20px;
                    height: 20px;
                    background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
                    border-radius: 10px;
                    font-size: 10px;
                    font-weight: 800;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 0 5px;
                    color: white;
                    border: 2px solid white;
                    box-shadow: 0 3px 12px rgba(43,103,119,0.4);
                    animation: pulse 2s infinite;
                }

                .user-avatar-header {
                    width: 44px;
                    height: 44px;
                    border-radius: 50%;
                    background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-weight: 800;
                    font-size: 18px;
                    color: white;
                    cursor: pointer;
                    border: 3px solid rgba(82,171,152,0.3);
                    transition: all 0.3s;
                    box-shadow: 0 6px 24px rgba(43,103,119,0.3);
                }

                .user-avatar-header:hover {
                    transform: scale(1.1);
                    box-shadow: 0 8px 32px rgba(43,103,119,0.5);
                }

                /* Content Wrapper */
                .content-wrapper {
                    flex: 1;
                    overflow-y: auto;
                    padding: 20px 16px;
                }

                .content-wrapper::-webkit-scrollbar {
                    width: 8px;
                }

                .content-wrapper::-webkit-scrollbar-track {
                    background: rgba(82,171,152,0.08);
                }

                .content-wrapper::-webkit-scrollbar-thumb {
                    background: rgba(82,171,152,0.3);
                    border-radius: 4px;
                }

                .content-area {
                    max-width: 1500px;
                    margin: 0 auto;
                    animation: slideIn 0.6s ease-out;
                }

                /* Hero Section */
                .hero-section {
                    background: linear-gradient(135deg, rgba(43,103,119,0.08) 0%, rgba(82,171,152,0.08) 100%);
                    border-radius: 12px;
                    padding: 28px 20px;
                    margin-bottom: 24px;
                    border: 1px solid rgba(82,171,152,0.15);
                    box-shadow: 0 2px 8px rgba(43,103,119,0.06);
                    position: relative;
                    overflow: hidden;
                }

                .hero-section::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    height: 4px;
                    background: linear-gradient(90deg, #2b6777 0%, #52ab98 100%);
                }

                .hero-content {
                    position: relative;
                    z-index: 1;
                }

                .hero-title {
                    font-size: 24px;
                    font-weight: 700;
                    color: #2b6777;
                    margin-bottom: 8px;
                    letter-spacing: -0.5px;
                }

                .hero-subtitle {
                    font-size: 13px;
                    color: rgba(43,103,119,0.6);
                    font-weight: 500;
                    line-height: 1.4;
                }

                /* Stats Grid */
                .stats-grid {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 12px;
                    margin-bottom: 24px;
                }

                .stat-card {
                    background: #ffffff;
                    border-radius: 12px;
                    padding: 20px 16px;
                    border: 1px solid rgba(43,103,119,0.12);
                    cursor: pointer;
                    transition: all 0.3s ease;
                    position: relative;
                    overflow: hidden;
                    box-shadow: 0 2px 8px rgba(43,103,119,0.06);
                }

                .stat-card::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 4px;
                    height: 100%;
                    background: linear-gradient(180deg, #2b6777 0%, #52ab98 100%);
                    transform: scaleY(0);
                    transition: transform 0.3s ease;
                }

                .stat-card:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 8px 24px rgba(43,103,119,0.12);
                    border-color: rgba(82,171,152,0.25);
                }

                .stat-card:hover::before {
                    transform: scaleY(1);
                }

                .stat-header {
                    display: flex;
                    align-items: flex-start;
                    justify-content: space-between;
                    margin-bottom: 16px;
                }

                .stat-label {
                    font-size: 10px;
                    color: rgba(43,103,119,0.6);
                    font-weight: 600;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .stat-icon {
                    width: 36px;
                    height: 36px;
                    border-radius: 8px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 18px;
                    background: rgba(82,171,152,0.08);
                    border: 1px solid rgba(82,171,152,0.15);
                }

                .stat-value {
                    font-size: 26px;
                    font-weight: 700;
                    color: #2b6777;
                    margin-bottom: 6px;
                    letter-spacing: -0.5px;
                    line-height: 1;
                }

                .stat-subtext {
                    font-size: 11px;
                    color: rgba(43,103,119,0.5);
                    font-weight: 500;
                }

                /* Section */
                .section {
                    background: rgba(255,255,255,0.9);
                    backdrop-filter: blur(30px);
                    border-radius: 16px;
                    padding: 24px 20px;
                    margin-bottom: 24px;
                    border: 2px solid rgba(82,171,152,0.2);
                    box-shadow: 0 4px 16px rgba(43,103,119,0.08);
                }

                .section-header {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    margin-bottom: 20px;
                    padding-bottom: 16px;
                    border-bottom: 2px solid rgba(82,171,152,0.15);
                }

                .section-icon {
                    width: 48px;
                    height: 48px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 24px;
                    background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
                    border: 2px solid rgba(82,171,152,0.3);
                    color: white;
                    box-shadow: 0 4px 16px rgba(43,103,119,0.2);
                    flex-shrink: 0;
                }

                .section-title {
                    font-size: 18px;
                    font-weight: 900;
                    color: #2b6777;
                }

                /* Room Info Grid */
                .room-info-grid {
                    display: grid;
                    grid-template-columns: 1fr;
                    gap: 12px;
                    margin-bottom: 20px;
                }

                .info-item {
                    display: flex;
                    align-items: flex-start;
                    gap: 12px;
                    padding: 16px 12px;
                    background: rgba(82,171,152,0.05);
                    border-radius: 12px;
                    border: 1.5px solid rgba(82,171,152,0.15);
                    transition: all 0.3s;
                }

                .info-item:hover {
                    background: rgba(82,171,152,0.1);
                    border-color: rgba(82,171,152,0.25);
                    transform: translateX(4px);
                }

                .info-icon {
                    width: 40px;
                    height: 40px;
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 20px;
                    background: rgba(255,255,255,0.8);
                    border: 1.5px solid rgba(82,171,152,0.25);
                    flex-shrink: 0;
                }

                .info-details {
                    flex: 1;
                }

                .info-label {
                    font-size: 10px;
                    color: rgba(43,103,119,0.6);
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    margin-bottom: 6px;
                }

                .info-value {
                    font-size: 16px;
                    font-weight: 800;
                    color: #2b6777;
                    margin-bottom: 4px;
                }

                .info-subtext {
                    font-size: 12px;
                    color: rgba(43,103,119,0.5);
                    font-weight: 600;
                }

                /* Status Badge */
                .status-badge {
                    display: inline-block;
                    padding: 6px 12px;
                    border-radius: 8px;
                    font-size: 12px;
                    font-weight: 800;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    background: rgba(76, 175, 80, 0.15);
                    color: #4caf50;
                    border: 1.5px solid rgba(76, 175, 80, 0.3);
                }

                /* Roommates Grid */
                .roommates-grid {
                    display: grid;
                    grid-template-columns: 1fr;
                    gap: 16px;
                }

                .roommate-card {
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    padding: 20px 16px;
                    background: rgba(82,171,152,0.05);
                    border-radius: 16px;
                    border: 1.5px solid rgba(82,171,152,0.15);
                    transition: all 0.3s;
                    position: relative;
                }

                .roommate-card.is-you {
                    background: linear-gradient(135deg, rgba(43,103,119,0.1) 0%, rgba(82,171,152,0.1) 100%);
                    border: 2px solid rgba(82,171,152,0.3);
                }

                .roommate-card:hover {
                    background: rgba(82,171,152,0.12);
                    border-color: rgba(82,171,152,0.3);
                    transform: translateX(4px);
                    box-shadow: 0 4px 16px rgba(43,103,119,0.12);
                }

                .you-badge {
                    position: absolute;
                    top: 8px;
                    right: 8px;
                    background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
                    color: white;
                    font-size: 10px;
                    font-weight: 800;
                    padding: 4px 10px;
                    border-radius: 6px;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .roommate-avatar {
                    width: 56px;
                    height: 56px;
                    border-radius: 50%;
                    background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-weight: 800;
                    font-size: 24px;
                    color: white;
                    border: 3px solid rgba(255,255,255,0.8);
                    box-shadow: 0 4px 16px rgba(43,103,119,0.2);
                    flex-shrink: 0;
                }

                .roommate-info {
                    flex: 1;
                }

                .roommate-name {
                    font-size: 16px;
                    font-weight: 900;
                    color: #2b6777;
                    margin-bottom: 6px;
                }

                .roommate-details {
                    font-size: 12px;
                    color: rgba(43,103,119,0.6);
                    margin-bottom: 4px;
                    font-weight: 600;
                }

                .roommate-contact {
                    font-size: 12px;
                    color: #52ab98;
                    font-weight: 700;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                }

                .empty-state {
                    text-align: center;
                    padding: 48px 20px;
                    color: rgba(43,103,119,0.5);
                }

                .empty-state-icon {
                    font-size: 48px;
                    margin-bottom: 16px;
                    opacity: 0.3;
                }

                .empty-state-text {
                    font-size: 16px;
                    font-weight: 600;
                    color: rgba(43,103,119,0.6);
                }

                /* Responsive - Tablet */
                @media (min-width: 768px) {
                    .top-header {
                        padding: 32px 24px;
                    }

                    .header-name {
                        font-size: 22px;
                    }

                    .header-greeting {
                        display: block;
                        font-size: 14px;
                    }

                    .header-title-section {
                        flex-direction: row;
                        align-items: center;
                        gap: 12px;
                    }

                    .content-wrapper {
                        padding: 32px 24px;
                    }

                    .hero-section {
                        padding: 48px 28px;
                    }

                    .hero-title {
                        font-size: 32px;
                    }

                    .hero-subtitle {
                        font-size: 15px;
                    }

                    .stats-grid {
                        grid-template-columns: repeat(4, 1fr);
                        gap: 16px;
                    }

                    .stat-card {
                        padding: 24px 20px;
                    }

                    .section {
                        padding: 32px 28px;
                    }

                    .section-header {
                        gap: 16px;
                        margin-bottom: 28px;
                    }

                    .section-icon {
                        width: 56px;
                        height: 56px;
                        font-size: 28px;
                    }

                    .section-title {
                        font-size: 22px;
                    }

                    .room-info-grid {
                        grid-template-columns: repeat(2, 1fr);
                        gap: 16px;
                    }

                    .info-item {
                        padding: 20px 16px;
                        gap: 16px;
                    }

                    .info-icon {
                        width: 48px;
                        height: 48px;
                        font-size: 24px;
                    }

                    .roommates-grid {
                        grid-template-columns: repeat(2, 1fr);
                        gap: 20px;
                    }

                    .roommate-card {
                        padding: 24px 20px;
                        gap: 20px;
                    }

                    .roommate-avatar {
                        width: 64px;
                        height: 64px;
                        font-size: 28px;
                    }
                }

                /* Responsive - Desktop */
                @media (min-width: 1024px) {
                    .sidebar {
                        position: static;
                        transform: translateX(0);
                    }

                    .close-btn {
                        display: none;
                    }

                    .overlay {
                        display: none !important;
                    }

                    .menu-toggle {
                        display: none;
                    }

                    .top-header {
                        padding: 52px 32px;
                    }

                    .header-name {
                        font-size: 28px;
                    }

                    .header-greeting {
                        font-size: 18px;
                    }

                    .content-wrapper {
                        padding: 56px 48px;
                    }

                    .hero-section {
                        padding: 90px 36px;
                    }

                    .hero-title {
                        font-size: 36px;
                    }

                    .hero-subtitle {
                        font-size: 16px;
                    }

                    .stats-grid {
                        gap: 20px;
                    }

                    .stat-card {
                        padding: 28px;
                    }

                    .stat-label {
                        font-size: 12px;
                    }

                    .stat-icon {
                        width: 48px;
                        height: 48px;
                        font-size: 24px;
                    }

                    .stat-value {
                        font-size: 32px;
                    }

                    .stat-subtext {
                        font-size: 13px;
                    }

                    .section {
                        padding: 40px;
                        border-radius: 28px;
                    }

                    .section-header {
                        gap: 20px;
                        margin-bottom: 32px;
                        padding-bottom: 24px;
                    }

                    .section-icon {
                        width: 64px;
                        height: 64px;
                        border-radius: 18px;
                        font-size: 32px;
                    }

                    .section-title {
                        font-size: 28px;
                    }

                    .room-info-grid {
                        grid-template-columns: repeat(3, 1fr);
                        gap: 24px;
                    }

                    .info-item {
                        padding: 24px;
                        gap: 16px;
                    }

                    .roommates-grid {
                        gap: 24px;
                    }

                    .roommate-card {
                        padding: 28px;
                        gap: 20px;
                        border-radius: 20px;
                    }

                    .you-badge {
                        top: 12px;
                        right: 12px;
                        font-size: 11px;
                        padding: 6px 12px;
                        border-radius: 8px;
                    }

                    .roommate-avatar {
                        width: 72px;
                        height: 72px;
                        font-size: 28px;
                    }

                    .roommate-name {
                        font-size: 20px;
                    }

                    .roommate-details {
                        font-size: 14px;
                    }

                    .roommate-contact {
                        font-size: 13px;
                    }
                }

                /* Small mobile adjustments */
                @media (max-width: 380px) {
                    .header-name {
                        font-size: 16px;
                    }

                    .hero-title {
                        font-size: 20px;
                    }

                    .stat-value {
                        font-size: 22px;
                    }
                }
            `}</style>

            <div className="dashboard-container">
                {/* Sidebar */}
                <div className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
                    <div className="sidebar-header">
                        <div className="sidebar-top">
                            <button onClick={() => setSidebarOpen(false)} className="close-btn">
                                ✕
                            </button>
                        </div>
                        <div className="user-info-sidebar">
                            <div className="user-avatar-sidebar">
                                {roomData?.fullName ? roomData.fullName.charAt(0).toUpperCase() : 'S'}
                            </div>
                            <div className="user-details-sidebar">
                                <div className="user-name-sidebar">{roomData?.fullName || 'Student Name'}</div>
                                <div className="user-role-sidebar">Student Portal</div>
                            </div>
                        </div>
                    </div>

                    <nav className="sidebar-nav">
                        <MenuItem icon="🏠" label="Dashboard" route="hostel-student-dashboard" />
                        <MenuItem icon="🚪" label="Entry/Exit Log" route="hostel-student-entry-exit-log" />
                        <MenuItem icon="🛏" label="Room Details" route="hostel-student-room-details" />
                        <MenuItem icon="🍽️" label="Food Preference" route="hostel-student-food-preference" />
                        <MenuItem icon="🧺" label="Laundry Service" route="hostel-student-laundry" />
                        <MenuItem icon="💳" label="Grievance" route="hostel-student-payments" />
                        <MenuItem icon="📝" label="Requests" route="hostel-student-requests" />
                        <MenuItem icon="📋" label="Requisition" route="hostel-student-requisition" />
                    </nav>

                    <div className="logout-section">
                        <button onClick={handleLogout} className="logout-btn">
                            🚪 Logout
                        </button>
                    </div>
                </div>

                {/* Overlays */}
                <div
                    className={`overlay ${sidebarOpen ? 'show' : ''}`}
                    onClick={() => setSidebarOpen(false)}
                />

                {/* Main Content */}
                <div className="main-content">
                    {/* Top Header */}
                    <div className="top-header">
                        <div className="header-left">
                            <button
                                className="menu-toggle"
                                onClick={() => setSidebarOpen(true)}
                            >
                                ☰
                            </button>
                            <div className="header-title-section">
                                <div className="header-greeting">Room Details</div>
                                <div className="header-name">{roomData?.fullName || 'Student'}</div>
                            </div>
                        </div>
                        <div className="header-right">
                            <div className="user-avatar-header">
                                {roomData?.fullName ? roomData.fullName.charAt(0).toUpperCase() : 'S'}
                            </div>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="content-wrapper">
                        <div className="content-area">
                            {/* Hero Section */}
                            <div className="hero-section">
                                <div className="hero-content">
                                    <h1 className="hero-title">Room Details 🏠</h1>
                                    <p className="hero-subtitle">
                                        Complete information about your hostel room
                                        {roomData?.hostelName && ` • ${roomData.hostelName}`}
                                    </p>
                                </div>
                            </div>

                            {/* Stats Grid - Only show if data exists */}
                            {stats.length > 0 && (
                                <div className="stats-grid">
                                    {stats.map((stat, index) => (
                                        <div key={index} className="stat-card">
                                            <div className="stat-header">
                                                <div className="stat-label">{stat.label}</div>
                                                <div className="stat-icon">{stat.icon}</div>
                                            </div>
                                            <div className="stat-value">{stat.value}</div>
                                            <div className="stat-subtext">{stat.subtext}</div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Room Information - Only show if relevant data exists */}
                            {roomData && (roomData.checkInDate || roomData.currentStatus) && (
                                <div className="section">
                                    <div className="section-header">
                                        <div className="section-icon">🏠</div>
                                        <div className="section-title">Room Information</div>
                                    </div>
                                    <div className="room-info-grid">
                                        {roomData.checkInDate && (
                                            <div className="info-item">
                                                <div className="info-icon">🗓️</div>
                                                <div className="info-details">
                                                    <div className="info-label">Allotted Date</div>
                                                    <div className="info-value">{formatDate(roomData.checkInDate)}</div>
                                                </div>
                                            </div>
                                        )}
                                        {roomData.currentStatus && (
                                            <div className="info-item">
                                                <div className="info-icon">📊</div>
                                                <div className="info-details">
                                                    <div className="info-label">Room Status</div>
                                                    <div className="info-value">
                                                        <span className="status-badge">
                                                            {roomData.currentStatus.replace('-', ' ').toUpperCase()}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        {roomData.bedNumber && (
                                            <div className="info-item">
                                                <div className="info-icon">🛏️</div>
                                                <div className="info-details">
                                                    <div className="info-label">Bed Number</div>
                                                    <div className="info-value">Bed {roomData.bedNumber}</div>
                                                    <div className="info-subtext">Assigned</div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Roommates - Only show current user */}
                            {roommates.length > 0 && (
                                <div className="section">
                                    <div className="section-header">
                                        <div className="section-icon">👥</div>
                                        <div className="section-title">Room Occupant</div>
                                    </div>
                                    <div className="roommates-grid">
                                        {roommates.map((roommate, index) => (
                                            <div key={index} className={`roommate-card ${roommate.isYou ? 'is-you' : ''}`}>
                                                {roommate.isYou && <div className="you-badge">You</div>}
                                                <div className="roommate-avatar">{roommate.avatar}</div>
                                                <div className="roommate-info">
                                                    <div className="roommate-name">{roommate.name}</div>
                                                    <div className="roommate-details">
                                                        {roommate.rollNo} • {roommate.department}
                                                    </div>
                                                    <div className="roommate-details">{roommate.year}</div>
                                                    <div className="roommate-contact">
                                                        📞 {roommate.contact}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Show empty state if no meaningful data */}
                            {!roomData && (
                                <div className="section">
                                    <div className="empty-state">
                                        <div className="empty-state-icon">🏠</div>
                                        <div className="empty-state-text">No room details available</div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default RoomDetails;